import React, { useState, useEffect, useCallback, useRef } from 'react';
import { deliveryDocumentImportService } from '../services';
import { DeliverySupplierResolver } from './DeliverySupplierResolver';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { useToast } from '@/hooks/use-toast';
import {
  UploadCloud, Loader2, FileText, Eye, Trash2, AlertTriangle, CheckCircle2, XCircle, HelpCircle,
  Lightbulb, ChevronDown, ChevronUp, Ban, X,
} from 'lucide-react';
import { getApiError } from '@/lib/utils';

const fmt = (val) => {
  if (val == null) return '\u2014';
  const n = Number(val);
  const abs = Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? '-' : ''}\u00A3${abs}`;
};

const WARNING_LABELS = {
  document_number_not_found: 'Document number could not be identified',
  document_date_not_found: 'Document date could not be identified',
  no_line_items_parsed: 'No line items could be parsed from this document',
  scanned_pdf_no_text_layer: 'This PDF is a scanned image with no selectable text — nothing could be extracted',
  unknown_layout_structured_fields_not_extracted: 'Unrecognised document layout — fields were not extracted',
  header_computed_total_mismatch: 'The printed totals do not match the sum of the extracted lines',
  header_total_not_found_for_reconciliation: 'No printed total was found to reconcile the lines against',
  non_zero_delivery_charge_on_document: 'A non-zero delivery charge line was found on this document',
};

const LINE_FLAG_LABELS = {
  split_unit_pack_ambiguity: 'Split case — partial pack, needs manual review',
  qty_unresolved: 'Quantity could not be determined',
  unit_price_qty_net_mismatch: 'Unit price \u00d7 qty does not match the printed net value',
  net_vat_gross_mismatch: 'Net + VAT does not match the printed gross value',
  unexpected_money_token_count: 'Unexpected number of money values on this line',
  pack_size_unresolved: 'Pack/measure size could not be determined — check the source page',
};

function humanizeLineFlag(code) {
  return LINE_FLAG_LABELS[code] || code;
}

function humanizeWarning(code) {
  if (WARNING_LABELS[code]) return WARNING_LABELS[code];
  const malformed = code.match(/^malformed_or_unreadable_pdf/);
  if (malformed) return 'This file could not be read as a PDF';
  const lineFlags = code.match(/^(\d+)_line\(s\)_flagged_for_review$/);
  if (lineFlags) return `${lineFlags[1]} line(s) need manual review (see highlighted rows below)`;
  const dup = code.match(/^duplicate_upload:(exact_file|same_document_number):import_(\d+)$/);
  if (dup) return `Duplicate of a previous upload (import #${dup[2]}, ${dup[1] === 'exact_file' ? 'identical file' : 'same document number'})`;
  const partialScan = code.match(/^partial_scan_pages:(.+)$/);
  if (partialScan) return `Page(s) ${partialScan[1]} are scanned images with no text layer`;
  return code;
}

const STATUS_BADGE = {
  extracted: { label: 'Extracted', className: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2 },
  needs_review: { label: 'Needs Review', className: 'bg-amber-100 text-amber-800 border-amber-300', icon: AlertTriangle },
  failed: { label: 'Failed', className: 'bg-rose-100 text-rose-800 border-rose-300', icon: XCircle },
  pending: { label: 'Pending', className: 'bg-slate-100 text-slate-700 border-slate-300', icon: HelpCircle },
  rejected: { label: 'Rejected — use manual entry', className: 'bg-red-100 text-red-800 border-red-300', icon: Ban },
};

const LAYOUT_LABEL = {
  procure_wizard: 'ProcureWizard',
  matthew_clark: 'Matthew Clark',
  unknown: 'Unknown layout',
};

// A record can be cleanly 'extracted' yet still need manual review (e.g. a
// duplicate upload) — the status badge must surface that, not just the raw
// extraction_status, otherwise a duplicate looks identical to a clean import.
function effectiveStatus(it) {
  if (it.extraction_status === 'extracted' && it.needs_manual_review) return 'needs_review';
  return it.extraction_status;
}

const CANDIDATE_CLASS_STYLE = {
  exact: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  strong_suggestion: 'bg-teal-100 text-teal-800 border-teal-300',
  review_required: 'bg-amber-100 text-amber-800 border-amber-300',
  low_confidence: 'bg-slate-100 text-slate-600 border-slate-300',
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.pending;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={cfg.className} data-testid={`ddi-status-badge-${status}`}>
      <Icon className="h-3 w-3 mr-1" />{cfg.label}
    </Badge>
  );
}

/**
 * Stocktake Step 2 — Delivery Document (PDF) Import.
 *
 * Read-only extraction review only: uploads a supplier invoice/credit-note
 * PDF, shows what the deterministic (no external AI) extractor found, and
 * flags anything ambiguous for manual review. Nothing here creates/posts a
 * Delivery or Credit note, and nothing here touches the Product Master —
 * the existing PSB matcher will consume this record in a later pass.
 *
 * Each line can also show inline, read-only Product Master candidate
 * suggestions (reusing the PSB matcher's name-matching signal). Purely
 * informational — there is no confirm/link action here; "remembered
 * pairs" and draft creation are deferred to a later pass.
 */
export default function DeliveryDocumentImportPanel({ sessionId, isEditable }) {
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [discardTarget, setDiscardTarget] = useState(null);
  const [discardingId, setDiscardingId] = useState(null);
  const [candidatesByLine, setCandidatesByLine] = useState({});
  const [expandedLineIds, setExpandedLineIds] = useState(new Set());
  const [rememberedMatches, setRememberedMatches] = useState({});
  const [pairConflicts, setPairConflicts] = useState({});

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const data = await deliveryDocumentImportService.list(sessionId);
      setItems(data.items || []);
    } catch (err) {
      toast({ title: 'Failed to load delivery document imports', description: getApiError(err), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [sessionId, toast]);

  useEffect(() => { load(); }, [load]);

  const scrollToManualEntry = () => {
    // Close the review dialog first — otherwise its overlay stays on top
    // of the native entry form we're scrolling to, so the user can see
    // the page move but can't actually click into the form underneath.
    setDetail(null);
    setTimeout(() => {
      document.getElementById('native-delivery-entry-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      const result = await deliveryDocumentImportService.upload(sessionId, file);
      setItems((prev) => [result, ...prev]);
      if (result.preflight_passed === false) {
        const desc = (result.rejection_reason_labels || []).join(' ') || 'This document could not be safely auto-reviewed.';
        toast({
          title: 'Rejected — use manual entry', description: `${file.name}: ${desc}`, variant: 'destructive',
        });
      } else if (result.needs_manual_review) {
        const desc = (result.warnings || []).map(humanizeWarning).join('; ') || 'See flagged lines below.';
        toast({ title: 'Uploaded — needs manual review', description: `${file.name}: ${desc}` });
      } else {
        toast({ title: 'Uploaded and extracted cleanly', description: `${file.name}: totals reconciled, ${result.line_count} line(s).` });
      }
    } catch (err) {
      toast({ title: 'Upload failed', description: getApiError(err), variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const loadRememberedMatches = async (importId) => {
    try {
      const data = await deliveryDocumentImportService.getRememberedMatches(importId);
      setRememberedMatches(data.matches || {});
    } catch (err) {
      // Non-critical — the "Suggest" candidates flow still works without this.
      setRememberedMatches({});
    }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    setCandidatesByLine({});
    setExpandedLineIds(new Set());
    setRememberedMatches({});
    setPairConflicts({});
    try {
      const data = await deliveryDocumentImportService.get(id);
      setDetail(data);
      if (data.reviewed_supplier_id) {
        loadRememberedMatches(id);
      }
    } catch (err) {
      toast({ title: 'Failed to load review', description: getApiError(err), variant: 'destructive' });
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleCandidates = async (lineId) => {
    setExpandedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId); else next.add(lineId);
      return next;
    });
    if (candidatesByLine[lineId] && !candidatesByLine[lineId].error) return;
    setCandidatesByLine((prev) => ({ ...prev, [lineId]: { loading: true } }));
    try {
      const data = await deliveryDocumentImportService.getLineCandidates(lineId);
      setCandidatesByLine((prev) => ({ ...prev, [lineId]: { loading: false, candidates: data.candidates || [] } }));
    } catch (err) {
      setCandidatesByLine((prev) => ({ ...prev, [lineId]: { loading: false, error: getApiError(err) } }));
    }
  };

  const [savingLineId, setSavingLineId] = useState(null);

  const selectReviewedProduct = async (lineId, productId, overwritePair = false) => {
    setSavingLineId(lineId);
    try {
      const updated = await deliveryDocumentImportService.setReviewedProduct(lineId, productId, overwritePair);
      setDetail((prev) => (prev ? {
        ...prev,
        lines: prev.lines.map((l) => (l.id === lineId ? { ...l, reviewed_product_id: updated.reviewed_product_id, reviewed_product: updated.reviewed_product } : l)),
      } : prev));
      if (productId) {
        // Collapse the candidates row once a choice has been made — the
        // green chip in the "Product Master" column now shows it clearly.
        setExpandedLineIds((prev) => { const next = new Set(prev); next.delete(lineId); return next; });
        // A used/applied line no longer needs its own remembered-match suggestion.
        setRememberedMatches((prev) => { const next = { ...prev }; delete next[lineId]; return next; });
      } else if (detail?.reviewed_supplier_id) {
        // Cleared back to unreviewed — re-check whether a remembered match
        // should reappear for this line instead of only the plain Suggest button.
        loadRememberedMatches(detail.id);
      }
      const teach = updated.pair_teach;
      if (teach?.status === 'conflict') {
        setPairConflicts((prev) => ({ ...prev, [lineId]: teach.existing_pair }));
      } else {
        setPairConflicts((prev) => { const next = { ...prev }; delete next[lineId]; return next; });
      }
      if (teach?.status === 'overwritten') {
        toast({ title: 'Remembered match updated', description: 'This supplier/SKU/pack will now suggest this product on future imports too.' });
      }
    } catch (err) {
      toast({ title: 'Could not save selection', description: getApiError(err), variant: 'destructive' });
    } finally {
      setSavingLineId(null);
    }
  };

  const handleDiscard = async (id) => {
    setDiscardingId(id);
    try {
      await deliveryDocumentImportService.discard(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
      setDetail((prev) => (prev && prev.id === id ? null : prev));
      toast({ title: 'Review record discarded' });
    } catch (err) {
      toast({ title: 'Failed to discard', description: getApiError(err), variant: 'destructive' });
    } finally {
      setDiscardingId(null);
      setDiscardTarget(null);
    }
  };

  return (
    <Card data-testid="delivery-document-import-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4" /> Import Delivery Documents (PDF)
        </CardTitle>
        <CardDescription>
          Upload a supplier invoice or credit note PDF for a read-only extraction review.
          This does NOT create a delivery/credit note or change the Product Master — review
          the extracted fields here first.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isEditable && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleFileSelect}
              data-testid="ddi-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="ddi-upload-button"
            >
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />}
              {uploading ? 'Uploading & extracting…' : 'Upload invoice / credit note PDF'}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground" data-testid="ddi-empty-state">No delivery documents uploaded for this session yet.</div>
        ) : (
          <div className="border rounded-md overflow-x-auto">
            <Table data-testid="ddi-import-table">
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Layout</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Doc #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id} data-testid={`ddi-row-${it.id}`}>
                    <TableCell className="max-w-[160px] truncate" title={it.original_filename}>{it.original_filename}</TableCell>
                    <TableCell>{LAYOUT_LABEL[it.layout_detected] || it.layout_detected}</TableCell>
                    <TableCell className="capitalize">{(it.document_type || 'unknown').replace('_', ' ')}</TableCell>
                    <TableCell>{it.document_number || '\u2014'}</TableCell>
                    <TableCell>{it.document_date || '\u2014'}</TableCell>
                    <TableCell className="max-w-[140px] truncate" title={it.supplier_name_raw}>{it.supplier_name_raw || '\u2014'}</TableCell>
                    <TableCell className="text-right">{it.preflight_passed === false ? '\u2014' : fmt(it.header_gross ?? it.computed_gross)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span title={(it.rejection_reason_labels || []).join(' ') || undefined}>
                          <StatusBadge status={effectiveStatus(it)} />
                        </span>
                        {it.is_duplicate && (
                          <Badge variant="outline" className="bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300 w-fit" data-testid={`ddi-duplicate-badge-${it.id}`}>
                            Duplicate ({it.duplicate_reason === 'exact_file' ? 'same file' : 'same doc #'})
                          </Badge>
                        )}
                        {it.totals_reconciled === false && (
                          <Badge variant="outline" className="bg-rose-100 text-rose-800 border-rose-300 w-fit">
                            Totals mismatch
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openDetail(it.id)} data-testid={`ddi-view-button-${it.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {isEditable && (
                          <Button
                            size="icon" variant="ghost" onClick={() => setDiscardTarget(it)}
                            disabled={discardingId === it.id}
                            data-testid={`ddi-discard-button-${it.id}`}
                          >
                            {discardingId === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-rose-600" />}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!discardTarget} onOpenChange={(open) => { if (!open) setDiscardTarget(null); }}>
        <AlertDialogContent data-testid="ddi-discard-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this review record?</AlertDialogTitle>
            <AlertDialogDescription>
              "{discardTarget?.original_filename}" will be permanently removed from this list. This only
              discards the extraction review — it never created a delivery/credit note, so nothing else is affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ddi-discard-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => discardTarget && handleDiscard(discardTarget.id)}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="ddi-discard-confirm"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!detail || detailLoading} onOpenChange={(open) => { if (!open) setDetail(null); }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="ddi-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.original_filename || 'Loading…'}</DialogTitle>
          </DialogHeader>
          {detailLoading && !detail ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading review…</div>
          ) : detail && (
            <div className="flex-1 min-h-0 overflow-y-auto pr-4" data-testid="ddi-detail-scroll-area">
              <div className="space-y-4">
                {detail.preflight_passed === false && (
                  <div className="border border-red-300 bg-red-50 rounded-md p-3 text-sm" data-testid="ddi-rejection-banner">
                    <div className="font-medium text-red-900 mb-1 flex items-center gap-1">
                      <Ban className="h-4 w-4" /> Rejected — not safe for automatic review
                    </div>
                    <ul className="list-disc list-inside text-red-800 text-xs space-y-0.5 mb-2">
                      {(detail.rejection_reason_labels || []).map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                    <p className="text-xs text-red-800 mb-2">
                      Nothing was guessed and no lines were stored for this document. Enter this delivery/credit
                      manually using the form below instead.
                    </p>
                    <Button
                      size="sm" variant="outline" className="border-red-400 text-red-900 hover:bg-red-100"
                      onClick={scrollToManualEntry} data-testid="ddi-rejection-manual-entry-cta"
                    >
                      Go to manual entry
                    </Button>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Layout</span><div className="font-medium">{LAYOUT_LABEL[detail.layout_detected] || detail.layout_detected}</div></div>
                  <div><span className="text-muted-foreground">Doc Type</span><div className="font-medium capitalize">{(detail.document_type || 'unknown').replace('_', ' ')}</div></div>
                  <div><span className="text-muted-foreground">Doc Number</span><div className="font-medium">{detail.document_number || '\u2014'}</div></div>
                  <div><span className="text-muted-foreground">Date</span><div className="font-medium">{detail.document_date || '\u2014'}</div></div>
                  <div><span className="text-muted-foreground">Account #</span><div className="font-medium">{detail.account_number_raw || '\u2014'}</div></div>
                  <div><span className="text-muted-foreground">Order #</span><div className="font-medium">{detail.order_number_raw || '\u2014'}</div></div>
                  <div><span className="text-muted-foreground">Pages</span><div className="font-medium">{detail.page_count}</div></div>
                </div>

                <div className="border rounded-md p-3" data-testid="ddi-supplier-field">
                  <div className="text-muted-foreground text-sm mb-1">Supplier</div>
                  {detail.preflight_passed === false ? (
                    <div className="font-medium text-sm">{detail.supplier_name_raw || '\u2014'}</div>
                  ) : (
                    <DeliverySupplierResolver
                      importId={detail.id}
                      supplierNameRaw={detail.supplier_name_raw}
                      reviewedSupplier={detail.reviewed_supplier}
                      isEditable={isEditable}
                      onChange={(supplier, supplierId) => {
                        setDetail((prev) => ({ ...prev, reviewed_supplier: supplier, reviewed_supplier_id: supplierId }));
                        if (supplierId) {
                          loadRememberedMatches(detail.id);
                        } else {
                          // Matches depend on a confirmed supplier identity —
                          // clearing it must clear any stale remembered-match
                          // chips/conflict banners, not leave them showing.
                          setRememberedMatches({});
                          setPairConflicts({});
                        }
                      }}
                    />
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="border rounded-md p-3" data-testid="ddi-delivered-to">
                    <div className="text-muted-foreground mb-1">Delivered to</div>
                    <pre className="whitespace-pre-wrap font-sans text-xs">{detail.delivered_to_raw || '\u2014'}</pre>
                  </div>
                  <div className="border rounded-md p-3" data-testid="ddi-invoiced-to">
                    <div className="text-muted-foreground mb-1">Invoiced to</div>
                    <pre className="whitespace-pre-wrap font-sans text-xs">{detail.invoiced_to_raw || '\u2014'}</pre>
                  </div>
                </div>

                {detail.preflight_passed !== false && (
                <div className="border rounded-md p-3 flex flex-wrap gap-4 text-sm" data-testid="ddi-reconciliation">
                  <div><span className="text-muted-foreground">Header Net/VAT/Gross</span><div className="font-medium">{fmt(detail.header_net)} / {fmt(detail.header_vat)} / {fmt(detail.header_gross)}</div></div>
                  <div><span className="text-muted-foreground">Computed Net/VAT/Gross</span><div className="font-medium">{fmt(detail.computed_net)} / {fmt(detail.computed_vat)} / {fmt(detail.computed_gross)}</div></div>
                  <div>
                    <span className="text-muted-foreground">Reconciled</span>
                    <div className="font-medium">
                      {detail.totals_reconciled === true && <span className="text-emerald-700">Yes (within £{Number(detail.totals_discrepancy ?? 0).toFixed(2)})</span>}
                      {detail.totals_reconciled === false && <span className="text-rose-700">No — discrepancy £{Number(detail.totals_discrepancy ?? 0).toFixed(2)}</span>}
                      {detail.totals_reconciled == null && <span className="text-slate-500">Not determinable</span>}
                    </div>
                  </div>
                </div>
                )}

                {detail.preflight_passed !== false && (detail.warnings || []).length > 0 && (
                  <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm" data-testid="ddi-warnings">
                    <div className="font-medium text-amber-800 mb-1 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Flagged for manual review</div>
                    <ul className="list-disc list-inside text-amber-800 text-xs space-y-0.5">
                      {detail.warnings.map((w, i) => <li key={i}>{humanizeWarning(w)}</li>)}
                    </ul>
                  </div>
                )}

                {detail.pages?.some((p) => p.is_scanned) && (
                  <div className="border border-rose-300 bg-rose-50 rounded-md p-2 text-xs text-rose-800" data-testid="ddi-scanned-warning">
                    Page(s) {detail.pages.filter((p) => p.is_scanned).map((p) => p.page_number).join(', ')} have no extractable text layer (scanned image) — nothing was guessed for these pages.
                  </div>
                )}

                {detail.preflight_passed !== false && (
                <div>
                  <div className="font-medium text-sm mb-2">Lines ({detail.lines?.length || 0})</div>
                  <div className="border rounded-md overflow-x-auto">
                    <Table data-testid="ddi-lines-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead>
                          <TableHead>Pg</TableHead>
                          <TableHead>Code</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Size</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Net</TableHead>
                          <TableHead className="text-right">VAT</TableHead>
                          <TableHead className="text-right">Gross</TableHead>
                          <TableHead>Flags</TableHead>
                          <TableHead>Product Master</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(detail.lines || []).map((l) => {
                          const expanded = expandedLineIds.has(l.id);
                          const candState = candidatesByLine[l.id];
                          return (
                          <React.Fragment key={l.id}>
                          <TableRow className={l.ambiguity_flags?.length ? 'bg-amber-50' : ''} data-testid={`ddi-line-${l.id}`}>
                            <TableCell>{l.line_sequence}</TableCell>
                            <TableCell>{l.page_number ?? '\u2014'}</TableCell>
                            <TableCell>{l.product_code_raw || '\u2014'}</TableCell>
                            <TableCell className="max-w-[220px] truncate" title={l.description_raw}>{l.description_raw}</TableCell>
                            <TableCell>{l.size_raw || '\u2014'}</TableCell>
                            <TableCell>{l.unit_raw || '\u2014'}</TableCell>
                            <TableCell className="text-right">{l.quantity ?? '\u2014'}</TableCell>
                            <TableCell className="text-right">{fmt(l.line_net)}</TableCell>
                            <TableCell className="text-right">{fmt(l.line_vat)}</TableCell>
                            <TableCell className="text-right">{fmt(l.line_gross)}</TableCell>
                            <TableCell>
                              {l.ambiguity_flags?.length > 0 ? (
                                <Badge
                                  variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]"
                                  title={l.ambiguity_flags.join(', ')} data-testid={`ddi-line-flag-${l.id}`}
                                >
                                  {l.ambiguity_flags.map(humanizeLineFlag).join('; ')}
                                </Badge>
                              ) : (
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              )}
                            </TableCell>
                            <TableCell>
                              {l.reviewed_product ? (
                                <div className="flex flex-col gap-1" data-testid={`ddi-line-reviewed-product-${l.id}`}>
                                  <div className="flex items-center gap-1 flex-wrap">
                                    <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">
                                      <CheckCircle2 className="h-3 w-3 mr-1 inline" />{l.reviewed_product.product_name}
                                    </Badge>
                                    <Button
                                      size="icon" variant="ghost" className="h-5 w-5" disabled={savingLineId === l.id}
                                      onClick={() => selectReviewedProduct(l.id, null)} data-testid={`ddi-clear-product-${l.id}`}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  {pairConflicts[l.id] && (
                                    <div className="flex items-center gap-1 flex-wrap text-[10px] bg-amber-50 border border-amber-300 rounded px-1.5 py-1" data-testid={`ddi-pair-conflict-${l.id}`}>
                                      <AlertTriangle className="h-3 w-3 text-amber-700 shrink-0" />
                                      <span className="text-amber-800">
                                        Differs from the remembered match ({pairConflicts[l.id].product?.product_name || 'unknown'}) for this supplier/SKU/pack.
                                      </span>
                                      {isEditable && (
                                        <Button
                                          size="sm" variant="outline" className="h-5 text-[10px]" disabled={savingLineId === l.id}
                                          onClick={() => selectReviewedProduct(l.id, l.reviewed_product.id, true)}
                                          data-testid={`ddi-overwrite-pair-${l.id}`}
                                        >
                                          Update remembered match
                                        </Button>
                                      )}
                                      <Button
                                        size="icon" variant="ghost" className="h-5 w-5"
                                        onClick={() => setPairConflicts((prev) => { const next = { ...prev }; delete next[l.id]; return next; })}
                                        data-testid={`ddi-dismiss-conflict-${l.id}`}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              ) : rememberedMatches[l.id] ? (
                                <div className="flex items-center gap-1 flex-wrap" data-testid={`ddi-remembered-match-${l.id}`}>
                                  <Badge
                                    variant="outline" className="bg-sky-100 text-sky-800 border-sky-300 text-[10px]"
                                    title={`Remembered match${rememberedMatches[l.id].confirmed_by ? ` — first confirmed by ${rememberedMatches[l.id].confirmed_by.name}` : ''}${rememberedMatches[l.id].confirmed_at ? ` on ${new Date(rememberedMatches[l.id].confirmed_at).toLocaleDateString()}` : ''}${rememberedMatches[l.id].source_import_filename ? ` (${rememberedMatches[l.id].source_import_filename})` : ''}`}
                                  >
                                    <Lightbulb className="h-3 w-3 mr-1 inline" />Remembered: {rememberedMatches[l.id].product?.product_name}
                                  </Badge>
                                  {isEditable && (
                                    <>
                                      <Button
                                        size="sm" className="h-6 text-[10px]" disabled={savingLineId === l.id}
                                        onClick={() => selectReviewedProduct(l.id, rememberedMatches[l.id].product.id)}
                                        data-testid={`ddi-use-remembered-${l.id}`}
                                      >
                                        {savingLineId === l.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Use'}
                                      </Button>
                                      <Button
                                        size="sm" variant="outline" className="h-6 text-[10px] gap-1"
                                        onClick={() => toggleCandidates(l.id)} data-testid={`ddi-remembered-not-this-${l.id}`}
                                      >
                                        Not this
                                        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                      </Button>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <Button
                                  size="sm" variant="outline" className="h-7 text-xs gap-1"
                                  onClick={() => toggleCandidates(l.id)}
                                  data-testid={`ddi-suggest-button-${l.id}`}
                                >
                                  <Lightbulb className="h-3 w-3" />
                                  Suggest
                                  {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                          {expanded && (
                            <TableRow data-testid={`ddi-candidates-row-${l.id}`}>
                              <TableCell colSpan={12} className="bg-slate-50 p-3">
                                {candState?.loading ? (
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Looking up Product Master candidates…</div>
                                ) : candState?.error ? (
                                  <div className="text-xs text-rose-700">{candState.error}</div>
                                ) : (candState?.candidates || []).length === 0 ? (
                                  <div className="text-xs text-muted-foreground">No Product Master candidates found for this line — informational only, nothing is linked automatically.</div>
                                ) : (
                                  <div className="space-y-1.5" data-testid={`ddi-candidates-list-${l.id}`}>
                                    <div className="text-xs text-muted-foreground mb-1">
                                      Matched against Product Master via the same name-matching signal the PSB matcher uses. {isEditable ? 'Select one to record your reviewed choice — nothing else is written or posted.' : 'Suggestions only.'}
                                    </div>
                                    {candState.candidates.map((c) => (
                                      <div key={c.product_id} className="flex items-center justify-between gap-3 text-xs bg-white border rounded px-2 py-1" data-testid={`ddi-candidate-${l.id}-${c.product_id}`}>
                                        <div className="flex items-center gap-2 min-w-0">
                                          <Badge variant="outline" className={`text-[10px] ${CANDIDATE_CLASS_STYLE[c.classification] || ''}`}>
                                            {Math.round(c.score * 100)}%
                                          </Badge>
                                          <span className="font-medium truncate">{c.product_name}</span>
                                          {c.brand && <span className="text-muted-foreground truncate">({c.brand})</span>}
                                        </div>
                                        <div className="flex items-center gap-3 text-muted-foreground shrink-0">
                                          <span>Code: {c.product_code || '\u2014'}</span>
                                          <span>{c.subcategory || '\u2014'}</span>
                                          {c.status !== 'active' && <Badge variant="outline" className="text-[10px]">{c.status}</Badge>}
                                          {isEditable && (
                                            <Button
                                              size="sm" className="h-6 text-xs" disabled={savingLineId === l.id}
                                              onClick={() => selectReviewedProduct(l.id, c.product_id)}
                                              data-testid={`ddi-select-candidate-${l.id}-${c.product_id}`}
                                            >
                                              {savingLineId === l.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Select'}
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
